// src/pages/BlogEdit.jsx
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/forum/Navbar';
import RichTextEditor from '../components/blog/RichTextEditor';

const BlogEdit = () => {
  const { id } = useParams();
  const { user, getAuthenticatedApi } = useAuth(); // Destructure getAuthenticatedApi
  const navigate = useNavigate();
  
  const [blog, setBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    tags: '',
    cover_image: null
  });
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      if (!user) {
        navigate('/login', { state: { from: `/blog/edit/${id}` } });
        return;
      }

      setIsLoading(true);
      try {
        const authenticatedApi = getAuthenticatedApi();
        // Use axios to fetch blog data
        const blogRes = await authenticatedApi.get(`/blog/${id}`);
        const blogData = blogRes.data;

        // Use the consistent user ID from the auth context
        const currentUserId = user.id || user.user_id;

        if (!blogData.author || blogData.author.id !== currentUserId) {
          alert('You are not authorized to edit this blog.');
          navigate('/blog', { replace: true });
          return;
        }

        setBlog(blogData);
        setFormData({
          title: blogData.title || '',
          content: blogData.content || '',
          tags: blogData.tags?.map(tag => tag.name).join(', ') || '',
          cover_image: null
        });

        if (blogData.cover_image) {
          setImagePreview(`https://app.writeapp.in${blogData.cover_image}`);
        }

      } catch (error) {
        console.error('Error fetching blog:', error);
        alert('Error loading blog for editing.');
        navigate('/blog', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchBlog();
    }, 100);

    return () => clearTimeout(timer);
  }, [id, navigate, user, getAuthenticatedApi]); // Add getAuthenticatedApi to dependency array

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'cover_image' && files?.[0]) {
      const file = files[0];
      setFormData(prev => ({ ...prev, cover_image: file }));
      
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    else if (formData.title.length > 200) newErrors.title = 'Title must be less than 200 characters';
    
    const textContent = formData.content.replace(/<[^>]*>/g, '').trim();
    if (!formData.content.trim()) newErrors.content = 'Content is required';
    else if (textContent.length < 50) newErrors.content = 'Content must be at least 50 characters long';
    
    if (formData.cover_image) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(formData.cover_image.type)) newErrors.cover_image = 'Please upload a valid image file (JPEG, PNG, or WebP)';
      else if (formData.cover_image.size > 5 * 1024 * 1024) newErrors.cover_image = 'Image file size must be less than 5MB';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title.trim());
      submitData.append('content', formData.content.trim());
      
      if (formData.tags.trim()) {
        const tags = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0).slice(0, 10);
        submitData.append('tags', JSON.stringify(tags));
      }
      
      if (formData.cover_image) {
        submitData.append('cover_image', formData.cover_image);
      }
      
      const authenticatedApi = getAuthenticatedApi();
      const response = await authenticatedApi.put(`/blog/${blog.id}/`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.status === 200) {
        navigate(`/blog/${blog.slug || blog.id}`, { 
          state: { success: 'Blog updated successfully!' } 
        });
      }
    } catch (error) {
      console.error('Error updating blog:', error);
      if (error.response?.data?.detail) {
        setErrors({ submit: error.response.data.detail });
      } else {
        setErrors({ submit: 'Failed to update blog. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, cover_image: null }));
    setImagePreview(blog.cover_image ? `https://app.writeapp.in${blog.cover_image}` : null);
    const fileInput = document.getElementById('cover_image');
    if (fileInput) fileInput.value = '';
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen bg-[#0B0C0D] text-white">
        <Navbar currentPage="blog" />
        <div className="flex flex-col justify-center items-center h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-400">Loading blog for editing...</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-[#0B0C0D] text-white">
        <Navbar currentPage="blog" />
        <div className="flex flex-col justify-center items-center h-[calc(100vh-4rem)]">
          <h2 className="text-2xl font-bold mb-4">Blog not found</h2>
          <Link to="/blog" className="text-primary hover:underline">Back to Blogs</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C0D] text-white">
      <Navbar currentPage="blog" />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to={`/blog/${id}`} className="group text-white py-1.5 rounded-md flex items-center w-fit transition-all duration-200 backdrop-blur-sm mb-4 bg-transparent">
          <svg className="w-4 h-4 mr-2 transform transition-transform duration-200 group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          <span className="text-sm">Back to Blog</span>
        </Link>
        <h1 className="text-3xl md:text-4xl font-bold mb-8">Edit Blog</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          {errors.submit && (<div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-md">{errors.submit}</div>)}
          <div>
            <label htmlFor="title" className="block text-sm font-medium mb-2">Title *</label>
            <input type="text" id="title" name="title" value={formData.title} onChange={handleInputChange} className={`w-full bg-gray-800 text-white px-4 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${errors.title ? 'border border-red-500' : ''}`} placeholder="Enter your blog title..." maxLength={200}/>
            {errors.title && (<p className="text-red-400 text-sm mt-1">{errors.title}</p>)}
            <p className="text-gray-400 text-sm mt-1">{formData.title.length}/200 characters</p>
          </div>
          <div>
            <label htmlFor="cover_image" className="block text-sm font-medium mb-2">Cover Image</label>
            <input type="file" id="cover_image" name="cover_image" onChange={handleInputChange} accept="image/jpeg,image/jpg,image/png,image/webp" className="w-full bg-gray-800 text-white px-4 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-primary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-primary file:text-white file:cursor-pointer" />
            {errors.cover_image && (<p className="text-red-400 text-sm mt-1">{errors.cover_image}</p>)}
            <p className="text-gray-400 text-sm mt-1">Supported formats: JPEG, PNG, WebP. Max size: 5MB</p>
            {imagePreview && (
              <div className="mt-4 relative inline-block">
                <img src={imagePreview} alt="Preview" className="max-w-xs max-h-48 rounded-md object-cover" />
                <button type="button" onClick={removeImage} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors">×</button>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="content" className="block text-sm font-medium mb-2">Content *</label>
            <RichTextEditor
              value={formData.content}
              onChange={(newContent) => setFormData(prev => ({ ...prev, content: newContent }))}
              placeholder="Write your blog content here..."
              error={errors.content}
            />
            {errors.content && (<p className="text-red-400 text-sm mt-1">{errors.content}</p>)}
            <p className="text-gray-400 text-sm mt-1">{formData.content.replace(/<[^>]*>/g, '').length} characters (minimum 50 required)</p>
          </div>
          <div>
            <label htmlFor="tags" className="block text-sm font-medium mb-2">Tags</label>
            <input type="text" id="tags" name="tags" value={formData.tags} onChange={handleInputChange} className="w-full bg-gray-800 text-white px-4 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="graphology, handwriting, analysis (separate with commas)" />
            <p className="text-gray-400 text-sm mt-1">Add up to 10 tags separated by commas</p>
          </div>
          <div className="flex gap-4 pt-4">
            <button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-3 rounded-md transition-colors flex items-center">
              {isSubmitting ? (<><div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>Updating...</>) : ('Update Blog')}
            </button>
            <Link to={`/blog/${id}`} className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-md transition-colors">Cancel</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogEdit;